/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/manager/schedule/ShiftCellPopover.tsx
 * PURPOSE: Popover [13] "Chọn ca" (đa ca) + sub-popover [14] cảnh báo vi phạm +
 *          "Sửa" per-chip chọn lại mã ca (A1, biên bản họp 25/08 mục 1) +
 *          kéo-giãn kích thước ([RESIZE] Việc 2, cộng thêm cạnh [DRAG] Việc 3
 *          đổi vị trí đã có) — SC-009-SCHEDULE-GRID (Release v0 WP-6b)
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-15
 * COMPLIANCE: SDLC 6.5.0 + NQH Brand Design System + Zero Mock Tolerance
 * PILLAR: P1 (Stage 04 - BUILD) + P6 (UI Components)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: Manager — Gán/sửa/bỏ ca cho 1 ô lưới phân ca (NV × ngày)
 * - Impact: Compliance — D-6: mode "Chặn" (server 422) → chỉ hiện lý do + [Đã hiểu],
 *   KHÔNG có [Vẫn thêm ca]; mode "Chỉ cảnh báo" → cho qua + warnings.
 * - "Thêm" áp dụng phạm vi ghi hàng loạt CHỈ theo kéo-chọn vùng `[2b]`
 *   (`rangeEmployeeIds`, xem targetEmployeeIds trong commitStaged) — bộ lọc
 *   "Nhân viên" bền theo phiên KHÔNG mở rộng phạm vi ghi.
 * - A1 (2026-09-03, họp 25/08 mục 1): nút "Sửa" cạnh "Xoá" — Quản Lý chọn lại
 *   mã ca của ca đã gán thay vì xoá rồi tạo lại (POST /cells gửi kèm
 *   schedule_id + shift_template_id; ca PENDING không sửa — dùng Duyệt/Từ chối).
 * - [STAGED-COMMIT] (thay hẳn kiến trúc, quyết định ghi đè có chủ ý — xem
 *   changelog WIREFRAME §SC-009 [2]): "Thêm"/"Thêm khối giờ" KHÔNG còn gọi API
 *   ngay — chỉ đẩy vào danh sách chờ ghi cục bộ (staged). "Xong" mới thực sự
 *   ghi TẤT CẢ mục trong danh sách chờ (tuần tự, bulk-assign/bulk-assign-hours
 *   không hỗ trợ nhiều mã ca/khoảng ngày khác nhau trong 1 lần gọi). Ngoại lệ:
 *   "Sửa"/"Xoá"/"Duyệt"/"Từ chối"/"Nghỉ" vẫn ghi ngay như cũ — KHÔNG thuộc
 *   phạm vi thay đổi này (chỉ 2 luồng "Thêm" của popover này bị đổi).
 * - Stakeholders: Branch Managers
 *
 * @technical-architecture
 * - Layer: Presentation Layer (anchored, draggable, viewport-aware popover)
 * - Pattern: port hành vi nqhGrid.anchorTo (flip khi tràn đáy, kẹp trong màn),
 *   [POS-FIX] neo BÊN CẠNH anchor thay vì NGAY DƯỚI (tránh che chính ô đang
 *   thao tác khi thân popover dài gần bằng viewport) + kéo-thả tự do (drag
 *   handle = popoverHead) + kéo-giãn kích thước tự do ([RESIZE], resize handle
 *   = popoverResizeHandle góc dưới-phải, cộng thêm cạnh drag không thay thế) +
 *   combobox gõ-lọc-không-dấu (FIX-3) +
 *   openSubPopover (FIX-1, [13] giữ mở) + staged-commit (mảng chờ ghi cục bộ,
 *   xem @business-context [STAGED-COMMIT])
 * - Dependencies: useUpdateScheduleCell, useBulkAssignSchedule, managerApi types, scheduleShared
 *
 * @cross-references
 * - Mockup: docs/02-design/04-ui-ux-design/design-imports/ui-ux-nqhbot/v0/schedule/manager/grid.html [13][14]
 * - Positioning: .../schedule/shared/common.js (anchorTo) — [POS-FIX] khác bản
 *   gốc (neo cạnh thay vì dưới), xem anchorTo() bên dưới.
 * - Backend: backend/app/api/v2/endpoints/manager_schedule.py (POST /bulk-assign —
 *   mọi "Thêm ca" từ popover này đi qua đây, kèm overlap-check; phạm vi ghi
 *   nhiều-NV chỉ đến từ kéo-chọn vùng `[2b]`, xem @business-context ở trên)
 * - Wireframe: docs/02-design/04-ui-ux-design/persona-flows/WIREFRAME-V0-MASTER-REGISTRY.md
 *   §SC-009-SCHEDULE-GRID, popover [2] (CHANGELOG entry: staged-commit).
 * ===============================================================================
 */

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  useApproveScheduleProposal,
  useBulkAssignHoursSchedule,
  useBulkAssignSchedule,
  useBulkOffSchedule,
  useManagerBranches,
  useRejectScheduleProposal,
  useUpdateScheduleCell,
} from "../../../hooks/useManagerSchedule";
import type {
  BulkAssignResult,
  BulkOffResult,
  GridDayCell,
  GridShiftItem,
  ShiftCodeItem,
} from "../../../services/api/managerApi";
import { extractApiError, formatDayMonth, noDiacritics, parseISODate } from "./scheduleShared";
import { DateRangePicker } from "../../../components/ui/DateRangePicker";
import styles from "../ManagerSchedulePage.module.css";

/** Khoảng ngày phân ca tối đa/lần — khớp giới hạn backend (span_days > 62). */
const MAX_BULK_ASSIGN_SPAN_DAYS = 62;

/** [STAGED-COMMIT]: 1 mục trong danh sách chờ ghi — "Thêm"/"Thêm khối giờ" tạo
 * ra, "Xong" (commitStaged) tiêu thụ. `kind` phân biệt 2 API đích khác nhau
 * (bulkAssignSchedule theo mã ca vs bulkAssignHoursSchedule theo khối giờ tự
 * do) vì BulkAssignPayload chỉ nhận ĐÚNG 1 shift_template_id/1 khoảng ngày
 * mỗi lần gọi — nhiều mục khác mã/khác ngày ⇒ nhiều lệnh gọi tuần tự. */
export interface StagedShiftItem {
  id: string;
  kind: "code" | "hours";
  /** Nhãn DÒNG CHÍNH hiển thị trong danh sách chờ ghi — format "Mã · giờ",
   * thống nhất với chip "Ca đã gán trong ngày" (PO 2026-09-12). Build sẵn lúc
   * "Thêm" (không phụ thuộc branchesQuery còn dữ liệu lúc "Xong" hay không). */
  label: string;
  /** Dòng PHỤ — CHỈ khoảng ngày ("17/09" hoặc "17/09–20/09"). PO 2026-09-13:
   * TÁCH riêng khỏi tên chi nhánh (xem `branchName` dưới) — gộp chung 1 chuỗi
   * trước đây khiến khoảng ngày NHIỀU NGÀY (dài hơn) hiển thị lẫn/tràn với
   * tên chi nhánh. Snapshot lúc "Thêm", cùng lý do với `label`. */
  subLabel?: string;
  /** Dòng "Làm tại: {cơ sở}" — snapshot lúc "Thêm", TÁCH riêng dòng khỏi
   * `subLabel` (PO 2026-09-13). LUÔN có giá trị (không chỉ khi điều động):
   * = tên chi nhánh ĐÍCH khi có chọn "Chi nhánh làm việc" khác (điều động),
   * ngược lại = tên chi nhánh HIỆN TẠI (`currentBranchName` prop) — thống
   * nhất với "Ca đã gán trong ngày" đã hiện "Làm tại" cho MỌI ca, không chỉ
   * ca điều động (cùng đợt sửa). */
  branchName?: string | null;
  dateStart: string;
  dateEnd: string;
  targetBranchId: string;
  // kind === "code"
  shiftTemplateId?: string;
  shiftCode?: string | null;
  /** shift_type của mã ca (morning/afternoon/night…) — PO 2026-09-13: để
   * chip trong danh sách chờ ghi tô ĐÚNG màu giống chip "Ca đã gán" (qua
   * `contentChipClass`), không phải luôn 1 màu trung tính. null/undefined
   * ở kind "hours" (khối giờ tuỳ chỉnh không có shift_type — contentChipClass
   * tự rẽ nhánh OT/hourly qua `isOvertime`). */
  shiftType?: string | null;
  /** true nếu mã ca là "Ca nghỉ" — PO 2026-09-13: chip chờ ghi dùng
   * shiftChipDayOff (xám, viền nét đứt) giống hệt chip "Ca đã gán", không
   * tô màu theo shift_type khi là ca nghỉ. */
  isDayOff?: boolean;
  // kind === "hours"
  startTime?: string;
  endTime?: string;
  isOvertime?: boolean;
  /** Lỗi từ lần "Xong" TRƯỚC (server từ chối/skip mục này) — hiện lại NGAY
   * trong danh sách để người dùng biết mục nào cần sửa/bỏ, không mất trắng. */
  error?: string | null;
}

/** [R42-CLIENT]: bản port rút gọn của quickEntryParser.ts#isPastR42 (chỉ dùng
 * ở QuickEntryModal trước đây) — kiểm SỚM khi "Thêm" để phản hồi ngay, KHÔNG
 * thay thế cổng R42 thật ở server (bulk-assign vẫn là nguồn sự thật cuối —
 * xem commitStaged: lỗi server vẫn hiện lại per-mục dù đã qua cổng client này,
 * vì khoảng trễ giữa "Thêm" và "Xong" có thể khiến mốc trôi qua entre 2 bước). */
const isPastR42 = (dateISO: string, startTimeHHMM: string, now: Date = new Date()): boolean => {
  const [hh, mm] = startTimeHHMM.split(":").map((v) => parseInt(v, 10));
  const [yyyy, mo, dd] = dateISO.split("-").map((v) => parseInt(v, 10));
  const target = new Date(yyyy, (mo || 1) - 1, dd || 1, hh || 0, mm || 0, 0, 0);
  return now.getTime() >= target.getTime();
};

/** Thông báo khi `isPastR42` chặn — dùng chung ở cả handleStageCode (mã ca)
 * và handleStageHours (khối giờ), tránh lặp y hệt 1 chuỗi ở 2 nơi (PO
 * 2026-09-13 báo thông báo cũ "Ngày bắt đầu đã qua giờ vào ca — không thể
 * thêm (chặn theo quy định R42)." gây khó hiểu: không nêu RÕ ca bắt đầu lúc
 * mấy giờ/ngày nào, nên PO chọn đúng ngày hôm nay ("13/09") vẫn không hiểu
 * vì sao bị chặn — phải nêu thẳng mốc giờ+ngày bị chặn, không để người đọc
 * tự suy luận ngược. Bỏ mã nội bộ "R42" khỏi copy hiển thị cho Quản lý chi
 * nhánh (end-user): theo UI-LABEL-RULE-01 (UI-LABELS-VI-ENUM.md) + ngoại lệ
 * duy nhất ở NQH-BRAND-DESIGN-SYSTEM.md (mã chuẩn quốc tế/pháp lý — IANA,
 * ISO...), "R42" không thuộc diện được giữ nguyên mã khi hiển thị cho người
 * dùng cuối; mã này vẫn còn nguyên trong tên hàm `isPastR42` + comment
 * `[R42-CLIENT]` phía trên cho @coder truy vết. */
const r42BlockedMessage = (dateISO: string, startTimeHHMM: string) =>
  `Ca bắt đầu ${startTimeHHMM} ngày ${formatDayMonth(dateISO)} đã qua thời điểm hiện tại — không thể thêm.`;

/** PO 2026-09-13: "người dùng xem quy định R42 ở đâu, bổ sung mục giải
 * thích ở bên dưới giống màn hình Phân ca đang có" — bỏ mã "R42" khỏi
 * thông báo lỗi (xem `r42BlockedMessage`) làm câu chữ tự nhiên hơn, NHƯNG
 * nếu không giải thích gì thêm thì người dùng không biết QUY TẮC CHUNG là
 * gì, chỉ biết đúng 1 ca cụ thể bị chặn. Theo ĐÚNG tiền lệ D-7
 * (ManagerSchedulePage.tsx, `.unpubPopupNote`) — "dòng giải thích NGAY
 * TRONG popup, không chỉ tooltip... tooltip cần hover, không hoạt động
 * trên mobile" — dòng chữ nhỏ (txtSm+muted) hiện NGAY DƯỚI 2 thông báo lỗi
 * "Thêm"/"Thêm khối giờ", KHÔNG phải tooltip. Chỉ hiện khi lỗi ĐANG hiện
 * ĐÚNG LÀ lỗi này (không đè lên các lỗi khác như "Chọn khoảng ngày."). */
const R42_EXPLANATION_NOTE =
  "Hệ thống chỉ cho phân ca vào các mốc còn ở tương lai — ca có giờ bắt đầu đã trôi qua (dù chọn đúng ngày hôm nay) sẽ không thêm được nữa.";
const isR42Message = (msg: string | null): boolean => !!msg && msg.startsWith("Ca bắt đầu ");

/** Hai khoảng ngày [aStart,aEnd] / [bStart,bEnd] (chuỗi ISO, so được trực
 * tiếp bằng lexicographic order) có giao nhau không — dùng để chặn staged
 * item trùng mã ca/khối giờ CHỒNG khoảng ngày (không chỉ trùng tuyệt đối). */
const rangesOverlap = (aStart: string, aEnd: string, bStart: string, bEnd: string) =>
  aStart <= bEnd && bStart <= aEnd;

const WarnIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} width={20} height={20} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
  </svg>
);

/** [X] (đối chiếu prototype sc009-build-prototype.html dòng 1654-1655/1791):
 * header popover thiếu nút đóng — port SAME icon path dùng ở
 * ManagerSchedulePage.tsx CloseIcon (không export nên copy nguyên path, tránh
 * coupling ngược trang cha vào component con). */
const CloseIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} width={18} height={18} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

export const shiftChipClass = (shiftType: string | null) => {
  switch (shiftType) {
    case "morning":
      return styles.shiftChipMorning;
    case "afternoon":
    case "evening":
      return styles.shiftChipAfter;
    case "night":
      return styles.shiftChipNight;
    default:
      return "";
  }
};

/** [OT] — màu nền chip cho khối "Phân theo giờ" (KHÔNG gắn mã ca: shift_code
 * lẫn shift_type đều null, khác ca theo mã ca/ca legacy free-form vẫn còn
 * shift_type). ANNOTATION [1-chip]: OT → nền turmeric; Phân theo giờ KHÔNG OT
 * → nền xám trung tính (gray-100), khác hẳn OT. Ca theo mã ca (hoặc free-form
 * còn shift_type) vẫn qua shiftChipClass(shift_type) như cũ — hàm này chỉ
 * QUYẾT ĐỊNH có phải khối giờ tuỳ chỉnh hay không rồi rẽ nhánh. */
export const contentChipClass = (shift: {
  shift_code?: string | null;
  shift_type?: string | null;
  is_overtime?: boolean;
}) => {
  const isHourlyBlock = !shift.shift_code && !shift.shift_type;
  if (isHourlyBlock) {
    return shift.is_overtime ? styles.shiftChipOt : styles.shiftChipHourly;
  }
  return shiftChipClass(shift.shift_type ?? null);
};

/** Nhãn option combobox: "M6 · Ca sáng 07:00–17:00" (không mã → tên ca + giờ).
 * A4 (2026-09-03): mã ngày nghỉ — "NGHI · Ca nghỉ" (mọi ngày trong tuần, ECO-DEC-2026-144). */
const shiftCodeLabel = (item: ShiftCodeItem) => {
  if (item.is_day_off) {
    return `${item.code ?? item.name} · Ca nghỉ`;
  }
  return item.code
    ? `${item.code} · ${item.name} ${item.start_time}–${item.end_time}`
    : `${item.name} ${item.start_time}–${item.end_time}`;
};

/** [STAGED-COMMIT] Nhãn dòng CHÍNH cho 1 mục trong "Sẽ ghi khi bấm 'Xong'" —
 * PO 2026-09-12: phải thống nhất format "Mã · giờ" với khối "Ca đã gán trong
 * ngày" (chip mã ca + giờ, KHÔNG có tên ca chen giữa). CỐ Ý không dùng
 * `shiftCodeLabel()` ở đây — hàm đó nhét thêm `item.name`, và với template
 * thật mà tên đã tự nhúng sẵn khung giờ trong ngoặc (vd tenant tự đặt tên
 * "Ca đêm E2E (22:00-06:00)"), kết quả là giờ HIỆN 2 LẦN (1 lần trong tên,
 * 1 lần do template nối `start_time–end_time`) — đúng lỗi Product Owner báo
 * (ảnh chụp thật 2026-09-12: dòng 1 "...Ca đêm E2E (22:00-06:00)", dòng 2 lặp
 * lại "22:00-06:00 · 13/09"). Không đọc `item.name` ⇒ không thể lặp. */
const stagedPrimaryLabel = (item: ShiftCodeItem) =>
  item.is_day_off
    ? `${item.code ?? item.name} · Ca nghỉ`
    : `${item.code ?? item.name} · ${item.start_time}–${item.end_time}`;

/** [POS-FIX]: Neo layer theo anchor — KHÁC bản port gốc nqhGrid.anchorTo
 * (common.js), vốn luôn neo NGAY DƯỚI/TRÊN anchor (left = anchor.left, tức
 * popover và ô đè CHUNG cột). Với thân popover dài gần bằng viewport (nhiều
 * ca + "Phân theo giờ" + danh sách chờ ghi), nhánh "kẹp trong viewport" của
 * bản gốc trải popover gần hết chiều cao màn hình → LUÔN đè lên đúng hàng
 * chứa ô đang thao tác dù neo trên hay dưới. Fix: ưu tiên neo BÊN CẠNH anchor
 * (phải trước, trái sau) — tách hẳn popover ra khỏi CỘT của ô đang thao tác
 * nên không hàng/chiều cao nào của popover có thể đè lên chính ô đó nữa; chỉ
 * rơi về "trên/dưới" (bản gốc) khi viewport hẹp tới mức không đủ chỗ cả 2
 * bên (ví dụ input phóng to hoặc màn hình quá nhỏ). */
export const anchorTo = (
  anchor: { left: number; top: number; bottom: number; right: number },
  width: number,
  height: number,
) => {
  const vw = document.documentElement.clientWidth;
  const vh = document.documentElement.clientHeight;
  const GAP = 8;

  // Dọc: căn ngang hàng với đỉnh ô, kẹp trong viewport (không cần lật trên/
  // dưới nữa vì hướng đè chính đã được giải quyết ở chiều ngang).
  const clampTop = (top: number) => Math.min(Math.max(GAP, top), Math.max(GAP, vh - height - GAP));

  // 1) Bên PHẢI ô: đủ chỗ (right + gap + width không tràn viewport)?
  if (anchor.right + 6 + width <= vw - GAP) {
    return { top: clampTop(anchor.top), left: anchor.right + 6 };
  }
  // 2) Bên TRÁI ô: đủ chỗ?
  if (anchor.left - 6 - width >= GAP) {
    return { top: clampTop(anchor.top), left: anchor.left - 6 - width };
  }
  // 3) Không đủ chỗ cả 2 bên (viewport hẹp) — rơi về hành vi gốc: NGAY DƯỚI ô,
  // lật LÊN TRÊN nếu tràn đáy, rồi kẹp trong màn.
  let left = anchor.left;
  const maxLeft = vw - width - GAP;
  if (left > maxLeft) left = Math.max(GAP, maxLeft);
  if (left < GAP) left = GAP;
  let top = anchor.bottom + 6;
  if (top + height > vh - GAP) {
    const above = anchor.top - height - 6;
    top = above >= GAP ? above : Math.max(GAP, vh - height - GAP);
  }
  return { top, left };
};

// [RESIZE] Việc 2 — sàn kích thước cho kéo-giãn tự do. 320px khớp đúng bề rộng
// nhỏ nhất từng dùng CHO POPOVER NÀY (xem lịch sử "320px → 440px → 480px" trong
// ManagerSchedulePage.module.css .popover) — dưới ngưỡng đó KHÔNG có cơ chế
// tự-vững (flex-wrap/nowrap/min-content, xem ShiftCellPopover.layout.test.tsx)
// nào từng được đo, nên không cho co hẹp hơn. 160px là sàn chiều cao tối
// thiểu để header + ít nhất 1 dòng thân + chân nút còn đọc được.
export const RESIZE_MIN_WIDTH = 320;
export const RESIZE_MIN_HEIGHT = 160;

interface ShiftCellPopoverProps {
  // [POS-FIX]: `right` bổ sung — cần biết mép phải ô để neo popover BÊN CẠNH
  // (không đè lên) thay vì NGAY DƯỚI ô, xem anchorTo().
  anchorRect: { left: number; top: number; bottom: number; right: number };
  userId: string;
  employeeName: string;
  day: GridDayCell;
  shiftCodes: ShiftCodeItem[];
  onClose: () => void;
  onToast: (message: string) => void;
  /** Bổ sung 2026-08-17: mở qua kéo-chọn khoảng ngày (ô trống) → khác day.date;
   * mở qua click ô thường → undefined, DateRangePicker mặc định 1 ngày (day.date). */
  initialRangeEnd?: string;
  /** Bổ sung 2026-08-17: "Thêm ca" giờ LUÔN qua bulk-assign (1 ngày = trường
   * hợp riêng của khoảng N ngày) — hợp nhất RangeAssignPopover vào đây, không
   * còn 2 modal khác nhau cho "ô đã có ca" vs "kéo-chọn khoảng ngày". */
  onBulkAssigned: (result: BulkAssignResult) => void;
  /** [2b]/[2f] (kéo-chọn hình chữ nhật ngày × nhân viên — BR-SCHD-013): danh
   * sách user_id của vùng chọn 2D (lia dọc qua nhiều hàng nhân viên trong
   * ManagerSchedulePage) — kết quả TƯỜNG MINH của MỘT lần kéo-chọn.
   *
   * Bộ lọc "Nhân viên" (multi-select, bền theo phiên) trên `ManagerSchedulePage`
   * là VIEW-scope thuần (chỉ lọc hiển thị lưới) và KHÔNG được truyền vào
   * component này — phạm vi ghi hàng loạt CHỈ đến từ kéo-chọn vùng `[2b]`
   * (prop này), không có đường nào khác mở rộng "Thêm" ra nhiều NV. Xem
   * WIREFRAME-V0-MASTER-REGISTRY.md §SC-009 dòng ~3509: "Bộ lọc này là
   * VIEW-scope, KHÔNG phải ACTION-scope".
   */
  rangeEmployeeIds?: string[];
  /** PO 2026-09-13: "Cần hiển thị thông tin nhân viên nào đang được thêm ca
   * ... thay vì chỉ sửa title đổi từ tên cụ thể sang '{N} nhân viên'" — tên
   * ĐẦY ĐỦ từng NV trong vùng kéo-chọn (song song `rangeEmployeeIds`, cùng
   * thứ tự/độ dài — component này không có danh bạ NV để tự tra tên từ id).
   * Hiện trong banner phạm vi khi `isMultiEmployee`. */
  rangeEmployeeNames?: string[];
  /** PO 2026-09-13: tên chi nhánh ĐANG XEM (không phải chi nhánh điều động
   * đến) — dùng làm mặc định cho dòng "Làm tại" trong danh sách chờ ghi khi
   * chưa chọn "Chi nhánh làm việc" khác. Component này không tự biết chi
   * nhánh đang xem (chỉ có `branchesQuery` cho danh sách CHỌN điều động). */
  currentBranchName?: string | null;
  onBulkOff: (result: BulkOffResult) => void;
}

const ShiftCellPopover: React.FC<ShiftCellPopoverProps> = ({
  anchorRect,
  userId,
  employeeName,
  day,
  shiftCodes,
  onClose,
  onToast,
  initialRangeEnd,
  onBulkAssigned,
  rangeEmployeeIds,
  rangeEmployeeNames,
  currentBranchName,
  onBulkOff,
}) => {
  const cellMutation = useUpdateScheduleCell();
  const approveMutation = useApproveScheduleProposal();
  const rejectMutation = useRejectScheduleProposal();
  const bulkAssignMutation = useBulkAssignSchedule();
  const bulkAssignHoursMutation = useBulkAssignHoursSchedule();
  const bulkOffMutation = useBulkOffSchedule();
  const branchesQuery = useManagerBranches();
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const popoverRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: -9999, left: -9999 });
  const [warnPos, setWarnPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  // [DRAG] Việc 3 — kéo-thả popover làm phương án dự phòng khi [POS-FIX] neo
  // tự động vẫn che thứ gì đó cần xem. Offset CỘNG THÊM vào `pos` (anchorTo()),
  // không thay thế — mở popover mới (anchorRect đổi) luôn reset về 0 (xem
  // useLayoutEffect neo bên dưới), tránh mang lệch-kéo cũ sang lần mở sau.
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const dragStateRef = useRef<{ startX: number; startY: number; baseX: number; baseY: number } | null>(null);
  // [RESIZE] Việc 2 — kéo-giãn kích thước (rộng/hẹp, dài/ngắn), CỘNG THÊM cạnh
  // [DRAG] ở trên (đổi vị trí), không thay thế. `size === null` = chưa resize,
  // giữ nguyên hành vi mặc định hiện tại (width 440px cố định theo CSS, height
  // theo nội dung/`max-height: calc(100vh - 32px)`). Mở popover mới (anchorRect
  // đổi) reset về null (xem useLayoutEffect neo bên dưới) — cùng lý do
  // dragOffset reset: kích thước tuỳ-chỉnh ở ô CŨ không được mang sang ô MỚI.
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);
  const resizeStateRef = useRef<{
    startX: number;
    startY: number;
    baseWidth: number;
    baseHeight: number;
  } | null>(null);

  const [inputValue, setInputValue] = useState("");
  const [selected, setSelected] = useState<ShiftCodeItem | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [warnBlock, setWarnBlock] = useState<{ message: string; violations: string[] } | null>(null);
  // Chọn mã ca (combobox) rồi bấm "Xong" mà chưa bấm "Thêm" sẽ mất lựa chọn
  // đó nếu đóng thẳng — chặn lại bằng 1 bước xác nhận rõ ràng: "Xong" khi còn
  // `selected` chưa lưu sẽ hiện banner này thay vì đóng thẳng; "Quay lại" giữ
  // popover mở, "Đóng, bỏ lựa chọn" mới thực sự gọi onClose (xem
  // handleDoneClick/confirmDiscardAndClose bên dưới).
  const [pendingDiscardWarning, setPendingDiscardWarning] = useState(false);
  // [R42] (2026-09-15): "Nghỉ (bỏ hết ca)" có thể xoá MỘT PHẦN — khối chưa
  // bắt đầu bị xoá, khối đã bắt đầu bị giữ lại (`locked_count` > 0, xem
  // handleRest bên dưới). Khác mọi kết quả khác của popover này (autosave →
  // onToast rồi đóng NGAY), trường hợp này KHÔNG đóng popover — giữ mở để
  // Manager thấy dòng "Còn {N} khối đã bắt đầu, không xoá được." (WIREFRAME
  // §SC-009 [R42] "Hai tầng khoá") NGAY cạnh nút, đúng tiền lệ D-7
  // (`.unpubPopupNote`) "dòng giải thích trực tiếp, không phải toast/tooltip
  // vì toast biến mất nhanh + tooltip không hoạt động trên di động".
  const [restLockedNote, setRestLockedNote] = useState<string | null>(null);
  // A1 (2026-09-03, họp 25/08 mục 1): state "Sửa ca" per-chip — tách riêng khỏi
  // combobox "Thêm ca" để 2 thao tác không giẫm state của nhau.
  const [editingShiftId, setEditingShiftId] = useState<string | null>(null);
  const [editInput, setEditInput] = useState("");
  const [editSelected, setEditSelected] = useState<ShiftCodeItem | null>(null);
  const [editMenuOpen, setEditMenuOpen] = useState(false);
  // (2026-08-18, gộp #632: state "điều động" đơn-ngày (transferOrgId/
  // managerNote/transferBranchesQuery, 2026-08-12) đã bỏ — #632 thay hẳn
  // bằng targetBranchId + bulk-assign theo khoảng ngày, xem handleStageCode/commitStaged.)

  // Bổ sung 2026-08-17: khoảng ngày cho "Thêm ca" — mặc định đúng 1 ngày
  // (day.date) khi mở qua click thường, hoặc khoảng đã kéo-chọn.
  const [rangeStart, setRangeStart] = useState(day.date);
  const [rangeEnd, setRangeEnd] = useState(initialRangeEnd ?? day.date);
  const [targetBranchId, setTargetBranchId] = useState<string>("");
  const [addErrorMsg, setAddErrorMsg] = useState<string | null>(null);

  // [OT] khối "Phân theo giờ" `[2]`/`[2b]`: section ĐỘC LẬP với "＋ Thêm ca"
  // phía trên, dùng CHUNG rangeStart/rangeEnd/targetBranchId của popover
  // (KHÔNG dựng lịch/chi-nhánh riêng — 1 popover = 1 phạm vi ngày/chi-nhánh
  // áp dụng, đúng cách prototype dùng chung `POP.branch` cho cả 2 select).
  const [hourStart, setHourStart] = useState("");
  const [hourEnd, setHourEnd] = useState("");
  const [isOvertime, setIsOvertime] = useState(false);
  const [hourErrorMsg, setHourErrorMsg] = useState<string | null>(null);

  // [STAGED-COMMIT]: "Thêm"/"Thêm khối giờ" đẩy vào đây (KHÔNG gọi API) — chỉ
  // "Xong" (commitStaged) mới thực sự ghi. Mỗi mục chụp lại (snapshot) khoảng
  // ngày/chi nhánh TẠI THỜI ĐIỂM bấm "Thêm" — người dùng có thể đổi
  // DateRangePicker/chi nhánh giữa 2 lần "Thêm" để thêm nhiều mục khác khoảng
  // ngày nhau trong CÙNG 1 lần "Xong" (đúng yêu cầu "khác ngày/khác mã").
  const [staged, setStaged] = useState<StagedShiftItem[]>([]);
  const [isCommitting, setIsCommitting] = useState(false);
  const nextStagedId = useRef(0);

  // [STAGED-SCROLL] PO 2026-09-12: mỗi khi "Thêm"/"Thêm khối giờ" đẩy thêm 1
  // mục vào `staged`, tự cuộn thân popover xuống để mục mới hiện ngay — trước
  // đây người dùng phải tự cuộn tay để thấy "Sẽ ghi khi bấm Xong" đã cập
  // nhật, đặc biệt khi form phía trên (chọn mã ca/khoảng ngày/chi nhánh) đã
  // chiếm hết khung nhìn. CHỈ cuộn khi `staged` TĂNG (bấm "Thêm"), không cuộn
  // khi GIẢM (xoá 1 mục qua removeStaged) — tránh giật màn hình ngoài ý muốn
  // lúc đang dọn danh sách.
  const bodyRef = useRef<HTMLDivElement>(null);
  const prevStagedLenRef = useRef(0);
  useEffect(() => {
    const el = bodyRef.current;
    if (staged.length > prevStagedLenRef.current && el) {
      // jsdom (test env) không hiện thực Element.scrollTo — feature-detect
      // thay vì gọi thẳng, tránh TypeError làm rớt toàn bộ test "Thêm".
      if (typeof el.scrollTo === "function") {
        el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
      } else {
        el.scrollTop = el.scrollHeight;
      }
    }
    prevStagedLenRef.current = staged.length;
  }, [staged.length]);

  // Bổ sung 2026-08-17: nhãn khoảng ngày trên tiêu đề — chỉ hiện thêm khi
  // NGƯỜI DÙNG đã sửa DateRangePicker ra khỏi mặc định 1 ngày (day.date),
  // giữ nguyên tiêu đề gốc cho trường hợp phổ biến nhất (không đổi gì).
  const rangeLabel = useMemo(() => {
    if (rangeStart === day.date && rangeEnd === day.date) return null;
    const s = formatDayMonth(rangeStart);
    const e = formatDayMonth(rangeEnd);
    return rangeStart === rangeEnd ? s : `${s} – ${e}`;
  }, [rangeStart, rangeEnd, day.date]);
  const dayCount = useMemo(() => {
    const ms = parseISODate(rangeEnd).getTime() - parseISODate(rangeStart).getTime();
    return Math.round(ms / 86400000) + 1;
  }, [rangeStart, rangeEnd]);

  // Phạm vi ghi hàng loạt CHỈ đến từ kéo-chọn vùng `[2b]` (`rangeEmployeeIds`,
  // TƯỜNG MINH kết quả của MỘT lần kéo-chọn) — bộ lọc "Nhân viên" bền theo
  // phiên KHÔNG tham gia cơ chế này (xem docstring prop `rangeEmployeeIds`
  // phía trên). Click 1 ô bình thường (rangeEmployeeIds rỗng/1 phần tử) LUÔN
  // chỉ gán cho đúng userId của ô đang mở.
  const targetEmployeeIds =
    rangeEmployeeIds && rangeEmployeeIds.length > 1 ? rangeEmployeeIds : [userId];
  const isMultiEmployee = targetEmployeeIds.length > 1;
  // [2f] BR-SCHD-013: popover ở "chế độ vùng chọn" (rectangle) khi kéo-chọn
  // trải >1 ngày HOẶC >1 nhân viên — lúc đó "Nghỉ (bỏ hết ca)" đơn-ô không còn
  // ý nghĩa (thao tác đó chỉ biết 1 ô), thay bằng "Nghỉ hàng loạt" có xác nhận.
  const isRangeMode = dayCount > 1 || isMultiEmployee;
  const [bulkOffConfirming, setBulkOffConfirming] = useState(false);

  // PO 2026-09-13: header chỉ còn "Chọn ca" — toàn bộ đối tượng/ngày/chú
  // thích phạm vi (trước đây in thẳng trên header) gộp vào đây, hiện qua
  // `title` (tooltip) trên span header. Cùng dữ liệu/logic đếm với header cũ
  // — chỉ đổi NƠI hiện, không đổi nội dung.
  const headerDetailText = `${isMultiEmployee ? `${targetEmployeeIds.length} nhân viên` : employeeName} · ${
    day.day_label
  } ${formatDayMonth(day.date)}${rangeLabel ? ` (đang thêm cho ${rangeLabel}, ${dayCount} ngày)` : ""}`;

  // Neo [13] cạnh ô ([POS-FIX], viewport-aware — xem anchorTo()). `anchorRect`
  // đổi (mở popover mới trên ô khác) → reset luôn `dragOffset` (Việc 3): kéo
  // lệch ở lần mở TRƯỚC không được mang sang ô MỚI đang mở.
  useLayoutEffect(() => {
    const el = popoverRef.current;
    if (!el) return;
    const w = el.offsetWidth || 320;
    const h = el.offsetHeight || 200;
    setPos(anchorTo(anchorRect, w, h));
    setDragOffset({ x: 0, y: 0 });
    setSize(null); // [RESIZE] Việc 2 — ô MỚI không kế thừa kích thước tuỳ-chỉnh của ô CŨ.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorRect]);

  // Nội dung thân đổi chiều cao (thêm/bớt ca, mở cảnh báo, đổi khoảng ngày,
  // thêm/bớt mục trong danh sách chờ ghi [STAGED-COMMIT]) → đo lại + neo lại,
  // GIỮ NGUYÊN `dragOffset` hiện có (khác effect trên — effect trên chỉ chạy
  // khi đổi SANG Ô KHÁC, effect này chạy khi NỘI DUNG cùng popover đổi).
  useLayoutEffect(() => {
    const el = popoverRef.current;
    if (!el) return;
    const w = el.offsetWidth || 320;
    const h = el.offsetHeight || 200;
    setPos(anchorTo(anchorRect, w, h));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day.shifts.length, warnBlock, rangeLabel, staged.length]);

  // [DRAG] Việc 3: kéo-thả tự do — header (`popoverHead`) là tay cầm kéo.
  // Mouse Events (mousedown/mousemove/mouseup, theo đúng yêu cầu "dùng cơ chế
  // đơn giản") — công cụ này (Bảng phân ca) là màn desktop, không cần chạm;
  // giới hạn trong viewport để không kéo popover ra ngoài màn hình mất dấu.
  const handleDragMouseMove = useCallback((e: MouseEvent) => {
    const drag = dragStateRef.current;
    const el = popoverRef.current;
    if (!drag || !el) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const vw = document.documentElement.clientWidth;
    const vh = document.documentElement.clientHeight;
    let nextX = drag.baseX + (e.clientX - drag.startX);
    let nextY = drag.baseY + (e.clientY - drag.startY);
    // Kẹp trong viewport: vị trí THẬT = pos + offset phải nằm trong [0, vw-w]/[0, vh-h].
    const minX = -pos.left;
    const maxX = vw - w - pos.left;
    const minY = -pos.top;
    const maxY = vh - h - pos.top;
    nextX = Math.min(Math.max(nextX, minX), Math.max(minX, maxX));
    nextY = Math.min(Math.max(nextY, minY), Math.max(minY, maxY));
    setDragOffset({ x: nextX, y: nextY });
  }, [pos]);

  const handleDragMouseUp = useCallback(() => {
    dragStateRef.current = null;
    window.removeEventListener("mousemove", handleDragMouseMove);
    window.removeEventListener("mouseup", handleDragMouseUp);
  }, [handleDragMouseMove]);

  const handleDragMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return; // chỉ kéo bằng nút chuột trái
    e.preventDefault(); // chặn browser bôi-chọn văn bản khi rê chuột qua nội dung popover lúc đang kéo
    dragStateRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      baseX: dragOffset.x,
      baseY: dragOffset.y,
    };
    window.addEventListener("mousemove", handleDragMouseMove);
    window.addEventListener("mouseup", handleDragMouseUp);
  };

  // Dọn listener kéo-thả nếu component unmount giữa chừng lúc đang kéo.
  useEffect(() => {
    return () => {
      window.removeEventListener("mousemove", handleDragMouseMove);
      window.removeEventListener("mouseup", handleDragMouseUp);
    };
  }, [handleDragMouseMove, handleDragMouseUp]);

  // [RESIZE] Việc 2: kéo-giãn kích thước — tay cầm ở góc dưới-phải
  // (`popoverResizeHandle`), CỘNG THÊM cạnh [DRAG] ở trên (đổi vị trí), không
  // thay thế. Cùng cơ chế Mouse Events + cùng cách kẹp-trong-viewport như
  // handleDragMouseMove, chỉ khác đối tượng đo là width/height thay vì
  // top/left. Base đo từ `size` hiện có nếu đã từng resize, ngược lại đo
  // `offsetWidth/offsetHeight` thật của popover (chưa resize lần nào).
  const handleResizeMouseMove = useCallback(
    (e: MouseEvent) => {
      const resize = resizeStateRef.current;
      const el = popoverRef.current;
      if (!resize || !el) return;
      const vw = document.documentElement.clientWidth;
      const vh = document.documentElement.clientHeight;
      // Chỗ còn lại của viewport tính từ vị trí THẬT hiện tại (pos + dragOffset)
      // — giống hệt cách handleDragMouseMove kẹp vị trí theo `pos`.
      const left = pos.left + dragOffset.x;
      const top = pos.top + dragOffset.y;
      const maxWidth = Math.max(RESIZE_MIN_WIDTH, vw - left - 8);
      const maxHeight = Math.max(RESIZE_MIN_HEIGHT, vh - top - 8);
      const rawWidth = resize.baseWidth + (e.clientX - resize.startX);
      const rawHeight = resize.baseHeight + (e.clientY - resize.startY);
      setSize({
        width: Math.min(Math.max(rawWidth, RESIZE_MIN_WIDTH), maxWidth),
        height: Math.min(Math.max(rawHeight, RESIZE_MIN_HEIGHT), maxHeight),
      });
    },
    [pos, dragOffset],
  );

  const handleResizeMouseUp = useCallback(() => {
    resizeStateRef.current = null;
    window.removeEventListener("mousemove", handleResizeMouseMove);
    window.removeEventListener("mouseup", handleResizeMouseUp);
  }, [handleResizeMouseMove]);

  const handleResizeMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return; // chỉ resize bằng nút chuột trái
    // Tay cầm resize nằm ở góc dưới-phải, tách biệt vùng với popoverHead (góc
    // trên) — rủi ro chồng vùng thấp, nhưng vẫn chặn nổi bọt để mousedown này
    // không kích hoạt luôn handleDragMouseDown nếu 2 vùng có ngày nào đó chồng
    // nhau (vd popover co lại rất thấp).
    e.stopPropagation();
    e.preventDefault(); // chặn browser bôi-chọn văn bản khi rê chuột qua nội dung popover lúc đang kéo-giãn
    const el = popoverRef.current;
    if (!el) return;
    resizeStateRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      baseWidth: size?.width ?? el.offsetWidth,
      baseHeight: size?.height ?? el.offsetHeight,
    };
    window.addEventListener("mousemove", handleResizeMouseMove);
    window.addEventListener("mouseup", handleResizeMouseUp);
  };

  // Dọn listener resize nếu component unmount giữa chừng lúc đang kéo-giãn.
  useEffect(() => {
    return () => {
      window.removeEventListener("mousemove", handleResizeMouseMove);
      window.removeEventListener("mouseup", handleResizeMouseUp);
    };
  }, [handleResizeMouseMove, handleResizeMouseUp]);

  // Esc đóng [13] (Esc trong combobox đã stopPropagation — chỉ đóng menu).
  // Esc là một đường đóng khác của handleRequestClose nên phải dùng CÙNG
  // guard (`selected` HOẶC `staged` còn mục) — mở rộng [STAGED-COMMIT]: nếu
  // không sẽ mất lựa chọn/mục chờ ghi chưa lưu mà không cảnh báo.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (isCommitting) return;
      if (selected || staged.length > 0) {
        setPendingDiscardWarning(true);
        return;
      }
      onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, selected, staged.length, isCommitting]);

  // Click ngoài [13]+[14] → đóng (pattern G.close()). Dùng CÙNG guard mở rộng
  // — click ra ngoài với lựa chọn/mục chờ ghi chưa lưu cũng không được huỷ
  // ngầm, phải qua banner xác nhận (pendingDiscardWarning).
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popoverRef.current && !popoverRef.current.contains(target)) {
        if (isCommitting) return;
        if (selected || staged.length > 0) {
          setPendingDiscardWarning(true);
          return;
        }
        onClose();
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [onClose, selected, staged.length, isCommitting]);

  const assigned = day.shifts;

  // Mã ca ĐÃ gán trong ngày (theo mã, hoặc theo khung giờ khi không có mã) —
  // dùng chung cho CẢ combobox "Thêm ca" lẫn "Sửa ca" (A1).
  const isCodeTaken = useCallback(
    (item: ShiftCodeItem) =>
      assigned.some(
        (s) =>
          (item.code != null && s.shift_code === item.code) ||
          (s.start_time === item.start_time && s.end_time === item.end_time),
      ),
    [assigned],
  );

  // [DUP-STAGED]: combobox "＋ Thêm ca" phải loại CẢ mã đã có trong `staged`
  // trùng khoảng ngày đang chọn — KHÔNG gộp vào `isCodeTaken` dùng chung với
  // "Sửa ca" (editOptions bên dưới) vì "Sửa ca" áp cho ca đã gán thật trên
  // đúng `day.date`, không liên quan gì tới danh sách chờ ghi/khoảng ngày của
  // "Thêm ca" — gộp chung sẽ khiến "Sửa ca" vô tình bị ảnh hưởng bởi staged.
  const isCodeTakenForAdd = useCallback(
    (item: ShiftCodeItem) =>
      isCodeTaken(item) ||
      staged.some(
        (s) =>
          s.kind === "code" &&
          s.shiftCode === (item.code ?? null) &&
          rangesOverlap(s.dateStart, s.dateEnd, rangeStart, rangeEnd),
      ),
    [isCodeTaken, staged, rangeStart, rangeEnd],
  );

  // Combobox: chỉ liệt kê mã ca CÒN LẠI (chưa gán trong ngày, CHƯA staged
  // trùng khoảng ngày) — annotation [13]
  const options = useMemo(() => {
    const q = noDiacritics(inputValue.trim());
    const remaining = shiftCodes.filter((item) => !isCodeTakenForAdd(item));
    const visible = remaining.filter((item) =>
      !q ? true : noDiacritics(shiftCodeLabel(item)).includes(q),
    );
    return { visible, allTaken: remaining.length === 0 && shiftCodes.length > 0 };
  }, [shiftCodes, inputValue, isCodeTakenForAdd]);

  // A1: options combobox "Sửa ca" — cùng luật lọc "đã gán" như Thêm ca; mã của
  // ca đang sửa NẰM TRONG `assigned` nên tự bị loại → chỉ chọn được mã KHÁC
  // (chọn lại đúng mã = không thay đổi gì, backend cũng trả noop).
  const editOptions = useMemo(() => {
    const q = noDiacritics(editInput.trim());
    return shiftCodes.filter(
      (item) => !isCodeTaken(item) && (!q || noDiacritics(shiftCodeLabel(item)).includes(q)),
    );
  }, [shiftCodes, editInput, isCodeTaken]);

  const resetCombo = () => {
    setSelected(null);
    setInputValue("");
    setMenuOpen(false);
    setPendingDiscardWarning(false);
  };

  const pickOption = (item: ShiftCodeItem) => {
    setSelected(item);
    setInputValue(shiftCodeLabel(item));
    setMenuOpen(false);
    setPendingDiscardWarning(false);
  };

  const openWarnBlock = (message: string, violations: string[]) => {
    // FIX-1: [14] neo NGAY DƯỚI [13] (lớp con), [13] KHÔNG đóng
    const el = popoverRef.current;
    if (el) {
      const r = el.getBoundingClientRect();
      setWarnPos(anchorTo({ left: r.left, top: r.top, bottom: r.bottom, right: r.right }, 320, 180));
    }
    setWarnBlock({ message, violations });
  };

  // ── A1 (2026-09-03, họp 25/08 mục 1): Sửa ca — chọn lại mã ca, không xoá- tạo lại ──
  const startEdit = (shift: GridShiftItem) => {
    setEditingShiftId(shift.schedule_id);
    setEditSelected(null);
    setEditInput("");
    setEditMenuOpen(true);
  };

  const cancelEdit = () => {
    setEditingShiftId(null);
    setEditSelected(null);
    setEditInput("");
    setEditMenuOpen(false);
  };

  const pickEditOption = (item: ShiftCodeItem) => {
    setEditSelected(item);
    setEditInput(shiftCodeLabel(item));
    setEditMenuOpen(false);
  };

  const handleSaveEdit = (shift: GridShiftItem) => {
    if (!editSelected || cellMutation.isPending) return;
    setEditMenuOpen(false);
    cellMutation.mutate(
      {
        user_id: userId,
        date: day.date,
        schedule_id: shift.schedule_id,
        shift_template_id: editSelected.id,
      },
      {
        onSuccess: (result) => {
          cancelEdit();
          if (result.warnings && result.warnings.length > 0) {
            // mode "Chỉ cảnh báo" — lưu được nhưng hiện [14] như Thêm ca
            openWarnBlock("Đã lưu ca kèm cảnh báo vi phạm ràng buộc:", result.warnings);
          } else {
            onToast("Đã lưu");
          }
        },
        onError: (error) => {
          const info = extractApiError(error);
          if (info.violations && info.violations.length > 0) {
            openWarnBlock(info.message, info.violations);
          } else {
            onToast(info.message);
          }
        },
      },
    );
  };

  // [STAGED-COMMIT]: nhãn chi nhánh snapshot lúc "Thêm" (không phụ thuộc
  // branchesQuery còn dữ liệu lúc "Xong" hay không — mạng có thể refetch/mất
  // cache giữa 2 thời điểm).
  const branchNameById = (id: string): string | null =>
    id ? branchesQuery.data?.items.find((b) => b.id === id)?.name ?? null : null;

  const stagedRangeText = (dateStart: string, dateEnd: string) =>
    dateStart === dateEnd
      ? formatDayMonth(dateStart)
      : `${formatDayMonth(dateStart)}–${formatDayMonth(dateEnd)}`;

  // [DUP-STAGED]: chặn thêm 1 mục TRÙNG mã ca (hoặc trùng khối giờ) CHỒNG
  // khoảng ngày với 1 mục đã có sẵn trong danh sách chờ ghi — không đợi tới
  // lúc "Xong" mới phát hiện dữ liệu rác tự thêm nhầm.
  const findStagedDuplicate = (
    kind: "code" | "hours",
    dateStart: string,
    dateEnd: string,
    match: { shiftCode?: string | null; startTime?: string; endTime?: string },
  ) =>
    staged.find((item) => {
      if (item.kind !== kind) return false;
      if (!rangesOverlap(item.dateStart, item.dateEnd, dateStart, dateEnd)) return false;
      return kind === "code"
        ? item.shiftCode === match.shiftCode
        : item.startTime === match.startTime && item.endTime === match.endTime;
    });

  // Bổ sung 2026-08-17 (hợp nhất RangeAssignPopover vào đây — "không được
  // làm 2 modal khác nhau"): "Thêm ca" áp dụng cho khoảng [rangeStart,
  // rangeEnd] (mặc định đúng 1 ngày = day.date) + chi nhánh đích tuỳ chọn
  // (điều động). [STAGED-COMMIT]: KHÔNG còn gọi API ở đây — chỉ đẩy vào
  // `staged`, chụp lại (snapshot) khoảng ngày/chi nhánh hiện tại của
  // DateRangePicker/select để người dùng đổi 2 giá trị đó cho lần "Thêm" kế
  // tiếp mà không ảnh hưởng mục đã thêm trước đó. "Xong" (commitStaged) mới
  // thực sự gọi bulk-assign.
  const handleStageCode = () => {
    if (!selected || isCommitting) return;
    if (!rangeStart || !rangeEnd) {
      setAddErrorMsg("Chọn khoảng ngày.");
      return;
    }
    // [R42-CLIENT]: phản hồi sớm — server (commitStaged) vẫn là cổng thật vì
    // khoảng trễ tới lúc "Xong" có thể khiến mốc trôi qua.
    if (isPastR42(rangeStart, selected.start_time)) {
      setAddErrorMsg(r42BlockedMessage(rangeStart, selected.start_time));
      return;
    }
    if (findStagedDuplicate("code", rangeStart, rangeEnd, { shiftCode: selected.code ?? null })) {
      setAddErrorMsg("Mã ca này đã có trong danh sách chờ ghi, trùng khoảng ngày đã chọn.");
      return;
    }
    setMenuOpen(false);
    setAddErrorMsg(null);
    // PO 2026-09-13: "Làm tại" LUÔN có giá trị — chi nhánh ĐÍCH khi có chọn
    // "Chi nhánh làm việc" khác (điều động), ngược lại chi nhánh HIỆN TẠI
    // (`currentBranchName` prop, không phải suy ra từ targetBranchId rỗng).
    const branchLabel = targetBranchId ? branchNameById(targetBranchId) : (currentBranchName ?? null);
    const item: StagedShiftItem = {
      id: `staged-${nextStagedId.current++}`,
      kind: "code",
      shiftTemplateId: selected.id,
      shiftCode: selected.code ?? null,
      shiftType: selected.shift_type ?? null,
      isDayOff: selected.is_day_off,
      label: stagedPrimaryLabel(selected),
      // Tách riêng khoảng ngày (subLabel) khỏi tên chi nhánh (branchName) —
      // PO 2026-09-13, tránh 1 chuỗi dài lẫn lộn khi chọn nhiều ngày.
      subLabel: stagedRangeText(rangeStart, rangeEnd),
      branchName: branchLabel,
      dateStart: rangeStart,
      dateEnd: rangeEnd,
      targetBranchId,
      error: null,
    };
    setStaged((prev) => [...prev, item]);
    resetCombo();
  };

  // [OT] "Thêm khối giờ": khối giờ tuỳ chỉnh (KHÔNG gắn mã ca) + tuỳ chọn
  // "Tính là OT". Validate client (đủ giờ + không trùng) — khớp prototype
  // (POP.err={k:'HOURS',...}). [STAGED-COMMIT]: cùng kiến trúc với
  // handleStageCode — đẩy vào `staged` thay vì gọi API; trần OT 4h/ngày
  // (R43) vẫn là chặn cứng SERVER-SIDE (không tính được đúng phía trình
  // duyệt cho mode nhiều-ngày, cần tổng giờ OT các ngày KHÁC ngoài `day`
  // đang mở) nên chỉ hiện lại ở commitStaged khi server từ chối.
  const handleStageHours = () => {
    if (isCommitting) return;
    if (!hourStart || !hourEnd) {
      setHourErrorMsg("Nhập đủ Giờ bắt đầu và Giờ kết thúc.");
      return;
    }
    if (hourStart === hourEnd) {
      setHourErrorMsg("Giờ bắt đầu và giờ kết thúc không được trùng nhau.");
      return;
    }
    if (!rangeStart || !rangeEnd) {
      setHourErrorMsg("Chọn khoảng ngày.");
      return;
    }
    if (isPastR42(rangeStart, hourStart)) {
      setHourErrorMsg(r42BlockedMessage(rangeStart, hourStart));
      return;
    }
    if (findStagedDuplicate("hours", rangeStart, rangeEnd, { startTime: hourStart, endTime: hourEnd })) {
      setHourErrorMsg("Khối giờ này đã có trong danh sách chờ ghi, trùng khoảng ngày đã chọn.");
      return;
    }
    setHourErrorMsg(null);
    // PO 2026-09-13: cùng quy tắc "Làm tại" với handleStageCode — chi nhánh
    // ĐÍCH khi điều động, ngược lại chi nhánh hiện tại.
    const branchLabel = targetBranchId ? branchNameById(targetBranchId) : (currentBranchName ?? null);
    const item: StagedShiftItem = {
      id: `staged-${nextStagedId.current++}`,
      kind: "hours",
      startTime: hourStart,
      endTime: hourEnd,
      isOvertime,
      label: `${hourStart}–${hourEnd}${isOvertime ? " · OT" : ""}`,
      subLabel: stagedRangeText(rangeStart, rangeEnd),
      branchName: branchLabel,
      dateStart: rangeStart,
      dateEnd: rangeEnd,
      targetBranchId,
      error: null,
    };
    setStaged((prev) => [...prev, item]);
    setHourStart("");
    setHourEnd("");
    setIsOvertime(false);
  };

  const removeStaged = (id: string) => setStaged((prev) => prev.filter((item) => item.id !== id));

  // [STAGED-COMMIT]: "Xong" — GHI THẬT tuần tự từng mục trong `staged` (bulk-
  // assign/bulk-assign-hours chỉ nhận 1 mã ca/1 khoảng ngày mỗi lần gọi, xem
  // @technical-architecture). Mục THÀNH CÔNG rời khỏi danh sách; mục LỖI (ném
  // exception, hoặc 200 nhưng assigned_count=0 — R42/overlap chặn hết, cùng
  // cách handleStageHours cũ kiểm tra) ở LẠI kèm `error` để người dùng thấy
  // đúng mục nào cần sửa/bỏ — KHÔNG mất trắng cả danh sách vì 1 mục lỗi.
  const commitStaged = async () => {
    if (staged.length === 0 || isCommitting) return;
    setIsCommitting(true);
    const remaining: StagedShiftItem[] = [];
    let successCount = 0;
    for (const item of staged) {
      try {
        if (item.kind === "code") {
          const result = await bulkAssignMutation.mutateAsync({
            employee_ids: targetEmployeeIds,
            shift_template_id: item.shiftTemplateId as string,
            date_start: item.dateStart,
            date_end: item.dateEnd,
            target_organization_id: item.targetBranchId || null,
          });
          if (result.assigned_count === 0 && result.skipped.length > 0) {
            remaining.push({ ...item, error: result.skipped[0].reason });
            continue;
          }
          successCount += 1;
          onBulkAssigned(result);
        } else {
          const result = await bulkAssignHoursMutation.mutateAsync({
            employee_ids: targetEmployeeIds,
            date_start: item.dateStart,
            date_end: item.dateEnd,
            start_time: item.startTime as string,
            end_time: item.endTime as string,
            is_overtime: !!item.isOvertime,
            target_organization_id: item.targetBranchId || null,
          });
          if (result.assigned_count === 0 && result.skipped.length > 0) {
            remaining.push({ ...item, error: result.skipped[0].reason });
            continue;
          }
          successCount += 1;
          onBulkAssigned(result);
        }
      } catch (error) {
        const info = extractApiError(error);
        const message =
          info.violations && info.violations.length > 0
            ? `${info.message} ${info.violations.join("; ")}`
            : info.message;
        remaining.push({ ...item, error: message });
      }
    }
    setStaged(remaining);
    setIsCommitting(false);
    if (remaining.length === 0) {
      onClose();
    } else if (successCount > 0) {
      onToast(`Đã lưu ${successCount}/${staged.length} mục. Còn ${remaining.length} mục lỗi, xem chi tiết bên dưới.`);
    } else {
      onToast(`Không lưu được mục nào (${remaining.length} mục lỗi). Xem chi tiết bên dưới.`);
    }
  };

  // "Xong": còn lựa chọn mã ca CHƯA bấm "Thêm" (chưa vào `staged`) → không
  // đóng/ghi ngay, hiện cảnh báo xác nhận trước (banner dùng chung với
  // handleRequestClose bên dưới). Ngược lại, còn mục trong `staged` → GHI
  // THẬT (commitStaged); rỗng cả 2 → đóng như cũ, không có gì để ghi.
  const handleDoneClick = () => {
    if (isCommitting) return;
    if (selected) {
      setPendingDiscardWarning(true);
      return;
    }
    if (staged.length > 0) {
      void commitStaged();
      return;
    }
    onClose();
  };

  // [STAGED-COMMIT]: X / Esc / click-ngoài — các đường ĐÓNG khác "Xong". Mở
  // rộng guard #771/BOT-029: trước chỉ canh `selected` (combobox), nay còn
  // phải canh CẢ `staged` (mục chờ ghi chưa bấm "Xong") — nếu không, người
  // dùng có thể mất N mục đã "Thêm" mà không được cảnh báo.
  const handleRequestClose = () => {
    if (isCommitting) return;
    if (selected || staged.length > 0) {
      setPendingDiscardWarning(true);
      return;
    }
    onClose();
  };

  const cancelDiscardWarning = () => setPendingDiscardWarning(false);

  // "Đóng, bỏ lựa chọn" — xác nhận rõ ràng: bỏ CẢ lựa chọn combobox chưa
  // "Thêm" LẪN mọi mục trong danh sách chờ ghi chưa "Xong" (banner đã liệt kê
  // đủ 2 loại nếu có, xem discardWarningMessage), rồi đóng thẳng KHÔNG ghi gì.
  const confirmDiscardAndClose = () => {
    setPendingDiscardWarning(false);
    resetCombo();
    setStaged([]);
    onClose();
  };

  // Nội dung banner cảnh báo — liệt đủ những gì sẽ MẤT nếu đóng ngay, tuỳ
  // theo đang treo cái gì (combobox chưa "Thêm", và/hoặc staged chưa "Xong").
  const discardWarningMessage = () => {
    const parts: string[] = [];
    if (selected) parts.push(`ca ${shiftCodeLabel(selected)} chưa bấm "Thêm"`);
    if (staged.length > 0) parts.push(`${staged.length} mục trong danh sách chờ ghi chưa bấm "Xong"`);
    return `Bạn còn ${parts.join(" và ")}. Đóng bây giờ sẽ mất toàn bộ.`;
  };

  const handleRemove = (shift: GridShiftItem) => {
    if (cellMutation.isPending) return;
    setRestLockedNote(null); // dòng "Còn N khối..." của lần "Nghỉ" trước có thể đã lệch sau thao tác mới
    cellMutation.mutate(
      { user_id: userId, date: day.date, schedule_id: shift.schedule_id },
      {
        // ISS-MGR-7 / BR-SCHD-008 R2: xoá ca cũng là 1 thao tác autosave —
        // cùng lấp khoảng trống phản hồi như commitStaged ở trên.
        onSuccess: () => onToast("Đã lưu"),
        onError: (error) => onToast(extractApiError(error).message),
      },
    );
  };

  // Employee tự đăng ký ca → Manager duyệt (2026-08-09) — ca PENDING dùng
  // Duyệt/Từ chối (có audit + lý do) thay vì Xoá thẳng.
  const handleApprove = (shift: GridShiftItem) => {
    if (approveMutation.isPending) return;
    approveMutation.mutate(shift.schedule_id, {
      onSuccess: () => onToast("Đã duyệt ca."),
      onError: (error) => onToast(extractApiError(error).message),
    });
  };

  const handleRest = () => {
    if (cellMutation.isPending) return;
    setRestLockedNote(null);
    cellMutation.mutate(
      { user_id: userId, date: day.date, remove: true },
      {
        onSuccess: (result) => {
          // [R42] `locked_count` > 0 = xoá MỘT PHẦN (khối đã bắt đầu bị giữ
          // lại) — không đóng popover, hiện dòng giải thích ngay cạnh nút
          // (xem khai báo `restLockedNote` phía trên). = 0 → xoá trọn vẹn,
          // giữ hành vi cũ (đóng ngay).
          if (result.locked_count > 0) {
            onToast("Đã lưu");
            setRestLockedNote(
              `Còn ${result.locked_count} khối đã bắt đầu, không xoá được.`,
            );
          } else {
            onClose();
          }
        },
        onError: (error) => onToast(extractApiError(error).message),
      },
    );
  };

  // [2f] BR-SCHD-013: "Nghỉ hàng loạt" — 2 bước, KHÔNG ghi ngay khi bấm nút
  // (khác handleRest đơn-ô) — wireframe [2f] "bắt buộc xác nhận trước khi ghi".
  const startBulkOff = () => setBulkOffConfirming(true);
  const cancelBulkOff = () => setBulkOffConfirming(false);
  const confirmBulkOff = () => {
    if (bulkOffMutation.isPending) return;
    bulkOffMutation.mutate(
      { employee_ids: targetEmployeeIds, date_start: rangeStart, date_end: rangeEnd },
      {
        onSuccess: (result) => {
          setBulkOffConfirming(false);
          onBulkOff(result);
          onClose();
        },
        onError: (error) => {
          setBulkOffConfirming(false);
          onToast(extractApiError(error).message);
        },
      },
    );
  };

  const startReject = (shift: GridShiftItem) => {
    setRejectingId(shift.schedule_id);
    setRejectReason("");
  };

  const cancelReject = () => {
    setRejectingId(null);
    setRejectReason("");
  };

  const confirmReject = (shift: GridShiftItem) => {
    if (rejectMutation.isPending || !rejectReason.trim()) return;
    rejectMutation.mutate(
      { scheduleId: shift.schedule_id, reason: rejectReason.trim() },
      {
        onSuccess: () => {
          setRejectingId(null);
          setRejectReason("");
          onToast("Đã từ chối đề xuất.");
        },
        onError: (error) => onToast(extractApiError(error).message),
      },
    );
  };

  return (
    <>
      {/* ══════════ [13] POPOVER "Chọn ca" (đa ca) — neo cạnh ô ══════════ */}
      <div
        ref={popoverRef}
        className={styles.popover}
        style={{
          top: pos.top + dragOffset.y,
          left: pos.left + dragOffset.x,
          // [RESIZE] Việc 2: size !== null nghĩa là người dùng đã tự kéo-giãn —
          // override width/max-height cố định của CSS; overflow:hidden để thân
          // (.popoverBody, đã có overflow-y:auto sẵn) tự cuộn bên trong khung
          // mới thay vì popover phình theo nội dung. size === null giữ NGUYÊN
          // hành vi mặc định hiện tại (không set gì, để class .popover tự quyết).
          ...(size ? { width: size.width, maxHeight: size.height, overflow: "hidden" } : {}),
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Chọn ca"
      >
        {/* [DRAG] Việc 3: header = tay cầm kéo (phương án dự phòng nếu
            [POS-FIX] neo tự động vẫn che thứ gì đó cần xem) — cursor báo hiệu
            kéo được, mousedown bắt đầu theo dõi mousemove/mouseup trên window
            (xem handleDragMouseDown). */}
        <div
          className={styles.popoverHead}
          style={{ display: "flex", alignItems: "center", gap: "var(--nqh-space-2)", cursor: "move" }}
          onMouseDown={handleDragMouseDown}
        >
          <span style={{ flex: 1 }} title={headerDetailText}>
            {/* PO 2026-09-13: "Title bỏ thông tin về đối tượng và khoảng
                ngày đi, các thông tin giải thích, annotation đưa vào tooltip
                cho gọn modal" — header trước đó LUÔN in kèm đối tượng (tên NV
                / "N nhân viên") + ngày + (khi kéo-chọn khoảng ngày) chú thích
                phạm vi ngay trên dòng chữ, chiếm chỗ popover. Giờ header CHỈ
                còn "Chọn ca" (đủ để nhận biết đang mở popover gì), toàn bộ
                phần đối tượng/ngày/chú thích chuyển vào `title` (native
                tooltip, hiện khi rê chuột) — build sẵn ở `headerDetailText`,
                cùng nguồn dữ liệu, không mất thông tin, chỉ đổi CÁCH hiện. */}
            Chọn ca
          </span>
          {/* [X] (đối chiếu prototype dòng 1654-1655/1791): header thiếu nút đóng.
              Đi qua guard handleRequestClose — X là một đường đóng popover,
              không được huỷ ngầm lựa chọn/mục chờ ghi chưa lưu. */}
          <button
            type="button"
            className={styles.btnIcon}
            aria-label="Đóng"
            disabled={isCommitting}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={handleRequestClose}
          >
            <CloseIcon />
          </button>
        </div>
        <div className={styles.popoverBody} ref={bodyRef}>
          {/* [5] (đối chiếu prototype dòng 1667/1673): tiêu đề LUÔN hiện, kể cả
              khi rỗng (không ca nào được gán). */}
          <div className={styles.popoverLabel}>Ca đã gán trong ngày</div>
          <div>
            {assigned.map((shift) => {
              const isPending = shift.approval_status === "PENDING";
              // [EXP] (2026-09-07): đề xuất PENDING đã qua giờ bắt đầu ca — wireframe
              // popover [2d](c): KHÔNG còn Duyệt/Từ chối, KHÔNG còn Sửa/Xoá (chỉ xem),
              // chỉ hiện dòng giải thích. Nhân viên thực tế có đi làm → xử lý ở SC-011.
              const isExpired = shift.approval_status === "EXPIRED";
              const isEditing = editingShiftId === shift.schedule_id;
              // [R42] Hai tầng khoá (WIREFRAME-V0-MASTER-REGISTRY.md §SC-009
              // [R42] "Hai tầng khoá độc lập" — mức DÒNG): khối MANAGER-tạo
              // (không phải đề xuất Employee — 2 nhánh đó có luồng riêng
              // isPending/isExpired ở trên, không đụng ở đây) đã qua giờ bắt
              // đầu → khoá [Sửa]/[Xoá]. Dùng LẠI đúng `isPastR42` đã có sẵn
              // (client-port của `_is_write_locked_by_r42` server) để nhất
              // quán với luồng "＋ Thêm ca" — không viết logic thời gian mới.
              // A4: ca ngày nghỉ (is_day_off) không có giờ bắt đầu thật —
              // KHÔNG áp R42, cùng quy tắc server (_is_write_locked_by_r42
              // caller luôn kèm `not template.is_day_off`).
              const isLocked = !shift.is_day_off && isPastR42(day.date, shift.start_time);
              return (
                <div className={styles.shiftLine} key={shift.schedule_id} style={{ flexDirection: "column", alignItems: "stretch", gap: "var(--nqh-space-2)" }}>
                  {isEditing ? (
                    /* A1: hàng "Sửa ca" — combobox chọn mã ca MỚI + Lưu/Huỷ */
                    <div style={{ display: "flex", alignItems: "center", gap: "var(--nqh-space-2)" }}>
                      <span className={styles.combo} style={{ flex: 1 }}>
                        <input
                          type="text"
                          className={styles.comboInput}
                          placeholder="Chọn mã ca mới… (vd: M6)"
                          autoComplete="off"
                          role="combobox"
                          aria-expanded={editMenuOpen}
                          aria-autocomplete="list"
                          aria-label="Chọn mã ca mới"
                          value={editInput}
                          autoFocus
                          onFocus={() => setEditMenuOpen(true)}
                          onChange={(e) => {
                            setEditSelected(null);
                            setEditInput(e.target.value);
                            setEditMenuOpen(true);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Escape" && editMenuOpen) {
                              e.stopPropagation();
                              e.preventDefault();
                              setEditMenuOpen(false);
                              return;
                            }
                            if (e.key === "Enter") {
                              e.preventDefault();
                              if (editMenuOpen && editOptions.length > 0) pickEditOption(editOptions[0]);
                            }
                          }}
                        />
                        {editMenuOpen && (
                          <div className={`${styles.menu} ${styles.comboMenu}`} role="listbox" aria-label="Chọn mã ca mới">
                            {editOptions.map((item) => (
                              <button
                                key={item.id}
                                type="button"
                                className={styles.menuItem}
                                role="option"
                                aria-selected={editSelected?.id === item.id}
                                onClick={() => pickEditOption(item)}
                              >
                                {shiftCodeLabel(item)}
                              </button>
                            ))}
                            {editOptions.length === 0 && (
                              <div className={styles.comboEmpty}>Không khớp mã ca nào</div>
                            )}
                          </div>
                        )}
                      </span>
                      <button
                        type="button"
                        className={`${styles.btn} ${styles.btnPrimary} ${styles.btnSm}`}
                        disabled={!editSelected || cellMutation.isPending}
                        onClick={() => handleSaveEdit(shift)}
                      >
                        {cellMutation.isPending ? "Đang lưu…" : "Lưu"}
                      </button>
                      <button
                        type="button"
                        className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`}
                        onClick={cancelEdit}
                      >
                        Huỷ
                      </button>
                    </div>
                  ) : (
                    /* Hàng ngang [nhãn ca | cụm nút]. Style nội tuyến CŨ
                       (display:flex + space-between) được thay bằng .shiftLineRow để
                       port cơ chế tự-vững của prototype: flex-wrap:wrap (hàng) +
                       .shiftLineLabel{white-space:nowrap} (nhãn) +
                       .shiftLineText{min-width:min-content} (cột chữ). Thiếu chỗ thì
                       CỤM NÚT rớt xuống dòng dưới, nhãn mã-giờ không bao giờ bị bóp
                       vỡ giữa chừng — xem khối comment ở .popover trong
                       ManagerSchedulePage.module.css để biết vì sao không giải bằng
                       cách nới px. */
                    <div className={styles.shiftLineRow}>
                      <span className={styles.shiftLineText}>
                        <span className={styles.shiftLineMeta}>
                          <span
                            className={`${styles.shiftChip} ${styles.shiftChipInline} ${
                              // A4 (2026-09-03): ca ngày nghỉ — chip xám "Ca nghỉ"
                              // [OT]: khối "Phân theo giờ" (không mã ca) →
                              // contentChipClass (turmeric OT / gray-100 thường)
                              // thay vì shiftChipClass(shift_type) trần.
                              shift.is_day_off ? styles.shiftChipDayOff : contentChipClass(shift)
                            } ${isPending ? styles.shiftChipPending : ""} ${
                              // [EXP] (2026-09-07): viền nét đứt xám riêng cho "Hết hạn"
                              isExpired ? styles.shiftChipExpired : ""
                            }`}
                          >
                            <span className={styles.shiftChipLine1}>
                              {shift.is_day_off ? "Ca nghỉ" : (shift.shift_code ?? shift.shift_label)}
                              {/* [OT] ANNOTATION [1-chip]: "· OT" khi có tick Tính-là-OT
                                  — CHỈ áp cho khối giờ tuỳ chỉnh (shift_code null),
                                  không rút gọn giờ ở bất kỳ đâu trong màn này. */}
                              {!shift.is_day_off && !shift.shift_code && shift.is_overtime ? " · OT" : ""}
                              {/* Khi shift_code null (khối "Phân theo giờ", không phải
                                  is_day_off), chip đã hiện `shift_label` làm nội dung
                                  chính (dòng trên) — lặp lại y hệt sẽ hiện
                                  "07:00–17:00 07:00–17:00" (trùng lặp vô nghĩa). Chỉ
                                  hiện label khi chip đang hiện MÃ CA (shift_code khác
                                  null). Nằm TRONG chip (không phải sibling ngoài) để
                                  nền màu ca phủ luôn cả khung giờ — PO yêu cầu 2026-09-12
                                  "đưa thông tin giờ vào trong vùng màu nền". */}
                              {!shift.is_day_off && shift.shift_code ? (
                                /* PORT `.pop-arow .lbl` (prototype dòng 342): "Ca xxx
                                   HH:mm–HH:mm" phải là MỘT khối không ngắt giữa chừng.
                                   Trước đây đây là text node trần — min-content của nó
                                   chỉ bằng TỪ DÀI NHẤT, nên khi hàng chật trình duyệt
                                   bẻ đôi nhãn (đúng ảnh lỗi Product Owner gửi) thay vì
                                   đẩy cụm nút xuống dòng dưới. */
                                <span className={styles.shiftLineLabel}> {shift.shift_label}</span>
                              ) : null}
                            </span>
                            {/* "Làm tại: {tên cơ sở}" — dòng 2 BÊN TRONG chip (PO
                                2026-09-12: "đưa thêm thông tin 'Làm tại:' bên dưới
                                thông tin ca" — vào cùng vùng nền màu ca, tiếp nối yêu
                                cầu trước đó cho dòng giờ). PO 2026-09-13: hiện cho CẢ
                                ca ở chi nhánh HIỆN TẠI, không riêng ca điều động —
                                backend đã trả `branch_name` cho MỌI ca từ trước (xem
                                comment GridShiftItem.branch_name trong
                                manager_schedule.py: "hiện trên MỌI chip đã gán ca,
                                không chỉ riêng ca điều động"), điều kiện `is_cross_branch
                                &&` ở FE trước đây tự ý thu hẹp lại, làm rớt thông tin
                                BE đã gửi sẵn. Điều kiện đúng chỉ cần `branch_name` tồn
                                tại, không cần is_cross_branch. Dùng LẠI đúng
                                styles.shiftLineBranch có sẵn (display:block, semibold,
                                đúng Registry §SC-009 [2] "dòng DUY NHẤT giữ đậm") — chỉ
                                đổi VỊ TRÍ trong JSX (vào trong chip thay vì sibling sau
                                .shiftLineMeta), không đổi style. Lý do điều động
                                (manager_note) giữ lại ở tooltip khi có (ca tại nhà
                                thường không có manager_note, tooltip đơn giản không
                                hiện). `.shiftChip` gốc là flex-column nên span này
                                (flex item thứ 2) tự xuống dòng dưới line1, không cần
                                CSS float/absolute nào thêm — đúng cơ chế
                                .shiftChipCrossBranch đã dùng cho chip lưới chính. */}
                            {shift.branch_name && (
                              <span
                                className={styles.shiftLineBranch}
                                title={shift.manager_note || undefined}
                              >
                                Làm tại: {shift.branch_name}
                              </span>
                            )}
                          </span>
                        </span>
                      </span>
                      {/* PO 2026-09-13: "Thêm thông tin ngày vào cột giữa cho
                          format ca đã gán luôn cho thống nhất" — đồng bộ với
                          cột ngày đã có ở section "Sẽ ghi khi bấm Xong"
                          (cùng vị trí: GIỮA nội dung ca và cụm nút). Ở khối
                          "Ca đã gán trong ngày" chỉ có 1 ngày duy nhất
                          (`day.date`, đúng ngày popover đang mở) nên mọi
                          dòng ca hiện CÙNG 1 giá trị — dư thừa xét riêng
                          màn này, nhưng đúng ý PO: đồng nhất hình dạng
                          3-cột giữa 2 section, không phải thêm thông tin
                          MỚI. `.popover` giờ tự nới rộng theo nội dung
                          (width:max-content) nên cột này KHÔNG giành chỗ
                          của nhãn ca/cụm nút — xem comment .shiftLineDate. */}
                      <span className={`${styles.shiftLineDate} ${styles.txtSm} ${styles.muted}`}>
                        {formatDayMonth(day.date)}
                      </span>
                      {isExpired ? null : isPending ? (
                        /* PORT `.pop-acts` (prototype dòng 346): flex:0 0 auto +
                           margin-left:auto — cụm nút không bao giờ bị bóp, luôn dạt
                           phải, và khi hàng hết chỗ thì RỚT NGUYÊN CỤM xuống dòng
                           dưới thay vì ép nhãn ca vỡ. */
                        <span className={styles.shiftLineActions}>
                          <button
                            type="button"
                            className={`${styles.btn} ${styles.btnPrimary} ${styles.btnSm}`}
                            disabled={approveMutation.isPending}
                            onClick={() => handleApprove(shift)}
                          >
                            Duyệt
                          </button>
                          <button
                            type="button"
                            className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`}
                            disabled={rejectMutation.isPending}
                            onClick={() => startReject(shift)}
                          >
                            Từ chối
                          </button>
                        </span>
                      ) : isLocked ? (
                        /* [R42] mức DÒNG (WIREFRAME §SC-009 [R42] "Hai tầng khoá
                           độc lập" (a)): khối này đã bắt đầu — server 422 CẢ
                           [Sửa] (update_grid_cell UPDATE path, `_is_write_locked_by_r42`
                           quanh dòng 3900) lẫn [Xoá] (REMOVE path, dòng 4020) —
                           ẩn cả hai, không chỉ hiện nút rồi để bấm hỏng (422 bất
                           ngờ không báo trước là đúng gap @reviewer phát hiện).
                           Nguyên văn wireframe chỉ nêu "[Xoá]" mất, nhưng backend
                           chặn cả [Sửa] — ẩn cả hai mới khớp hành vi thật. */
                        <span className={styles.shiftLineActions}>
                          <span className={styles.muted}>🔒 Đã bắt đầu</span>
                        </span>
                      ) : (
                        /* A1: [Sửa] cạnh [Xoá] — chọn lại mã ca thay vì xoá-tạo lại.
                           .shiftLineActions: PORT `.pop-acts` — xem ghi chú ở nhánh
                           [Duyệt][Từ chối] ngay trên. */
                        <span className={styles.shiftLineActions}>
                          <button
                            type="button"
                            className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`}
                            disabled={cellMutation.isPending}
                            onClick={() => startEdit(shift)}
                          >
                            Sửa
                          </button>
                          <button
                            type="button"
                            className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`}
                            disabled={cellMutation.isPending}
                            onClick={() => handleRemove(shift)}
                          >
                            Xoá
                          </button>
                        </span>
                      )}
                    </div>
                  )}
                  {isExpired && (
                    /* [2d](c): dòng giải thích duy nhất, không action nào — NHÃN VI đúng
                       nguyên văn wireframe (khớp entry trong danh sách "NHÃN VI + lỗi"). */
                    <span className={styles.muted}>
                      Hết hạn — đã qua giờ bắt đầu, không còn duyệt được. Nếu nhân viên thực
                      tế có đi làm, xử lý ở màn Chấm công.
                    </span>
                  )}
                  {isPending && (
                    <span className={styles.muted}>Employee tự đề xuất, chờ duyệt</span>
                  )}
                  {rejectingId === shift.schedule_id && (
                    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nqh-space-2)" }}>
                      <textarea
                        className={styles.comboInput}
                        placeholder="Lý do từ chối (bắt buộc)…"
                        rows={2}
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        autoFocus
                      />
                      <span style={{ display: "flex", gap: "var(--nqh-space-2)", justifyContent: "flex-end" }}>
                        <button
                          type="button"
                          className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`}
                          onClick={cancelReject}
                        >
                          Huỷ
                        </button>
                        <button
                          type="button"
                          className={`${styles.btn} ${styles.btnPrimary} ${styles.btnSm}`}
                          disabled={rejectMutation.isPending || !rejectReason.trim()}
                          onClick={() => confirmReject(shift)}
                        >
                          Xác nhận từ chối
                        </button>
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {assigned.length === 0 && (
            // [5]: copy đúng NHÃN VI wireframe verbatim ("Chưa có ca xếp cho
            // ngày này." — WIREFRAME-V0-MASTER-REGISTRY.md §SC-009 NHÃN VI,
            // dòng 3717).
            <div className={`${styles.txtSm} ${styles.muted}`}>Chưa có ca xếp cho ngày này.</div>
          )}
          {/* PO 2026-09-13: "Thêm thông tin nhân viên vào một section chuẩn
              trong modal luôn" + "tạo thêm section nhân viên được chọn ở
              trên section Từ ngày - đến ngày" — trước đây chỉ là 1 dòng chữ
              nhỏ ("Nhân viên: ...") kẹp giữa banner phạm vi và combobox
              "Thêm ca", không có nhãn section riêng. Giờ là section CHUẨN
              (cùng khuôn .popoverLabel với "Ca đã gán trong ngày"/"Thêm
              ca"/...), LUÔN hiện (không chỉ khi nhiều NV) — case 1 NV vẫn
              hiện đúng tên NV đó, giữ đối tượng đang thao tác luôn có mặt
              trong modal dù header đã bỏ (xem headerDetailText/tooltip
              header phía trên). Annotation phạm vi ("Đang áp dụng cho X
              ngày × N nhân viên...", PO: "đưa vào tooltip một cách hợp lý
              chứ không hiển thị thẳng trên modal") gắn làm `title` ngay
              trên nhãn section này — đây là section DUY NHẤT thực sự nói về
              "nhân viên", chỗ hợp lý nhất để giải thích phạm vi liên quan
              nhân viên+ngày. CHỈ gắn khi isRangeMode (đúng điều kiện banner
              cũ vốn dùng — không đổi khi nào có chú thích, chỉ đổi NƠI hiện). */}
          <div
            className={styles.popoverLabel}
            style={{ marginTop: "var(--nqh-space-3)" }}
            title={
              isRangeMode
                ? `Đang áp dụng cho ${dayCount} ngày × ${targetEmployeeIds.length} nhân viên (${formatDayMonth(
                    rangeStart,
                  )}–${formatDayMonth(rangeEnd)}) — ca sẽ gán cho TẤT CẢ ô trong vùng chọn khi bấm Thêm.`
                : undefined
            }
          >
            Nhân viên
          </div>
          {/* PO 2026-09-14: "Hiện theo chiều dọc xuống hàng cho dễ nhìn" —
              trước đây `rangeEmployeeNames.join(", ")` nhét cả danh sách vào
              1 dòng, nhiều NV thì dây dài không đọc nổi (nhất là code test
              dài kiểu "NV005 • E2E SC007 8awckr"). Mỗi NV giờ là 1 `<div>`
              riêng (block, tự xuống dòng) thay vì nối chuỗi bằng dấu phẩy. */}
          <div style={{ marginBottom: "var(--nqh-space-2)" }}>
            {isMultiEmployee && rangeEmployeeNames && rangeEmployeeNames.length === targetEmployeeIds.length
              ? rangeEmployeeNames.map((name, i) => (
                  <div key={i} className={styles.txtSm}>
                    {name}
                  </div>
                ))
              : <div className={styles.txtSm}>{employeeName}</div>}
          </div>

          {/* PO 2026-09-13: "Đưa section Từ ngày đến ngày lên trên section
              Thêm ca" — chọn KHOẢNG NGÀY trước khi chọn MÃ CA hợp lý hơn về
              luồng thao tác (biết áp dụng cho ngày nào rồi mới tìm ca phù
              hợp ngày đó), cũng khớp thứ tự Nhân viên → Ngày → Ca đang dựng
              lại ở đây. Bổ sung 2026-08-17 (hợp nhất RangeAssignPopover —
              "không được làm 2 modal khác nhau"): khoảng ngày cho "Thêm ca",
              mặc định đúng 1 ngày (day.date). */}
          <div className={styles.popoverLabel} style={{ marginTop: "var(--nqh-space-3)" }}>
            Từ ngày – đến ngày
          </div>
          <DateRangePicker
            value={{ start: rangeStart, end: rangeEnd }}
            onChange={(range) => {
              setRangeStart(range.start ?? day.date);
              setRangeEnd(range.end ?? range.start ?? day.date);
            }}
            maxSpanDays={MAX_BULK_ASSIGN_SPAN_DAYS}
            hidePresets
          />

          <div className={styles.popoverLabel} style={{ marginTop: "var(--nqh-space-3)" }}>
            Thêm ca
          </div>
          <div className={styles.addShift}>
            <span className={styles.combo}>
              <input
                type="text"
                className={styles.comboInput}
                placeholder="Nhập mã ca… (vd: M6)"
                autoComplete="off"
                role="combobox"
                aria-expanded={menuOpen}
                aria-autocomplete="list"
                aria-label="Nhập mã ca"
                value={inputValue}
                onFocus={() => setMenuOpen(true)}
                onChange={(e) => {
                  // gõ lại → huỷ lựa chọn hiện tại, bắt chọn lại từ danh sách
                  setSelected(null);
                  setInputValue(e.target.value);
                  setMenuOpen(true);
                  setPendingDiscardWarning(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Escape" && menuOpen) {
                    // đóng menu, KHÔNG đóng [13]
                    e.stopPropagation();
                    e.preventDefault();
                    setMenuOpen(false);
                    return;
                  }
                  if (e.key === "Enter") {
                    e.preventDefault();
                    // Enter chọn option khả dụng đầu tiên
                    if (menuOpen && options.visible.length > 0) pickOption(options.visible[0]);
                  }
                }}
              />
              {menuOpen && (
                <div className={`${styles.menu} ${styles.comboMenu}`} role="listbox" aria-label="Chọn mã ca">
                  {options.visible.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className={styles.menuItem}
                      role="option"
                      aria-selected={selected?.id === item.id}
                      onClick={() => pickOption(item)}
                    >
                      {shiftCodeLabel(item)}
                    </button>
                  ))}
                  {options.visible.length === 0 && (
                    <div className={styles.comboEmpty}>
                      {options.allTaken ? "Đã gán đủ các mã ca" : "Không khớp mã ca nào"}
                    </div>
                  )}
                </div>
              )}
            </span>
          </div>

          <div className={styles.popoverLabel} style={{ marginTop: "var(--nqh-space-3)" }}>
            Chi nhánh làm việc
          </div>
          <select
            className={styles.comboInput}
            value={targetBranchId}
            onChange={(e) => setTargetBranchId(e.target.value)}
          >
            <option value="">Chi nhánh hiện tại (không điều động)</option>
            {branchesQuery.data?.items.map((b) => (
              <option key={b.id} value={b.id}>
                Điều động sang: {b.name}
              </option>
            ))}
          </select>

          {addErrorMsg && (
            <p className={`${styles.muted} ${styles.popoverNote}`} style={{ color: "var(--nqh-chili)", marginTop: 8 }}>
              {addErrorMsg}
            </p>
          )}
          {isR42Message(addErrorMsg) && (
            <p className={`${styles.txtSm} ${styles.muted} ${styles.popoverNote}`} style={{ marginTop: 4 }}>
              {R42_EXPLANATION_NOTE}
            </p>
          )}

          {/* [STAGED-COMMIT]: "Thêm" nay CHỈ đẩy vào danh sách chờ ghi (local,
              không gọi API) — đổi từ btnPrimary → btnSecondary vì hành động
              GHI THẬT (có hệ quả không hoàn tác được) đã chuyển sang "Xong";
              btnPrimary (nổi bật) giờ thuộc về hành động đó, không phải hành
              động thêm-vào-hàng-chờ này nữa. */}
          <button
            type="button"
            className={`${styles.btn} ${styles.btnSecondary}`}
            style={{ marginTop: "var(--nqh-space-3)", width: "100%" }}
            disabled={!selected || isCommitting}
            aria-disabled={!selected || isCommitting}
            onClick={handleStageCode}
          >
            Thêm
          </button>

          {/* [OT] "Phân theo giờ" (wireframe ANNOTATION [OT] + [2]/[2b] mockup
              dòng 2830-2836/2860-2864; đối chiếu prototype
              sc009-build-prototype.html dòng 1762-1770): section ĐỘC LẬP với
              "＋ Thêm ca" phía trên — cách THỨ HAI thêm khối vào ngày, dùng
              song song, dùng chung khoảng ngày/chi nhánh của popover (không
              dựng lịch/select riêng — 1 popover = 1 phạm vi). */}
          <div className={styles.popoverLabel} style={{ marginTop: "var(--nqh-space-4)" }}>
            Phân theo giờ
          </div>
          <div className={styles.addShift}>
            <label htmlFor="pHourStart" className={`${styles.txtSm} ${styles.muted}`} style={{ display: "block" }}>
              Giờ bắt đầu
            </label>
            <input
              id="pHourStart"
              type="time"
              className={styles.comboInput}
              value={hourStart}
              onChange={(e) => {
                setHourStart(e.target.value);
                setHourErrorMsg(null);
              }}
            />
          </div>
          <div className={styles.addShift}>
            <label htmlFor="pHourEnd" className={`${styles.txtSm} ${styles.muted}`} style={{ display: "block" }}>
              Giờ kết thúc
            </label>
            <input
              id="pHourEnd"
              type="time"
              className={styles.comboInput}
              value={hourEnd}
              onChange={(e) => {
                setHourEnd(e.target.value);
                setHourErrorMsg(null);
              }}
            />
          </div>
          {/* PO 2026-09-14: "sau khi nhấn nút Thêm thì annotation lại không
              xuống hàng và đẩy chiều rộng của popover ra xa" — GỐC THẬT (đo
              bằng Chromium thật, không phải suy đoán): 2 đoạn lỗi/giải thích
              này TRƯỚC ĐÂY nằm BÊN TRONG `.addShift` (display:flex, không
              flex-wrap) — tức là FLEX-ROW SIBLING của <label>/<input>, không
              phải khối riêng "NGAY DƯỚI" như comment [4] mô tả. Với
              `.popover{width:max-content}`, trình duyệt tính bề rộng max-
              content của TOÀN hàng flex — kể cả khi <p> có `max-width`/
              `width` cố định, phép đo trực tiếp cho thấy nó VẪN kéo cả hàng
              theo (đã tự kiểm bằng cách tắt hiển thị từng phần tử con: kéo
              ra khỏi `.addShift` làm khối `<div>` RIÊNG (như addErrorMsg ở
              khối "＋ Thêm ca" phía trên vốn ĐÃ đúng cấu trúc này) đưa bề
              rộng về đúng 480px, class `.popoverNote` (max-width:400px)
              KHÔNG cần đổi gì thêm. */}
          {hourErrorMsg && (
            <p className={`${styles.muted} ${styles.popoverNote}`} style={{ color: "var(--nqh-chili)", marginTop: 4 }}>
              {hourErrorMsg}
            </p>
          )}
          {isR42Message(hourErrorMsg) && (
            <p className={`${styles.txtSm} ${styles.muted} ${styles.popoverNote}`} style={{ marginTop: 4 }}>
              {R42_EXPLANATION_NOTE}
            </p>
          )}

          {/* [2] single-cell mode CÓ "Làm tại" ở khối Phân theo giờ; [2b] vùng
              chọn KHÔNG có (mockup dòng 2860-2864) — dùng chung targetBranchId
              với "＋ Thêm ca" (đúng cách prototype dùng chung POP.branch cho
              cả #pBranch1/#pBranch2). */}
          {!isRangeMode && (
            <>
              <div className={styles.popoverLabel} style={{ marginTop: "var(--nqh-space-3)" }}>
                Làm tại
              </div>
              <select
                className={styles.comboInput}
                value={targetBranchId}
                onChange={(e) => setTargetBranchId(e.target.value)}
              >
                <option value="">Chi nhánh hiện tại (không điều động)</option>
                {branchesQuery.data?.items.map((b) => (
                  <option key={b.id} value={b.id}>
                    Điều động sang: {b.name}
                  </option>
                ))}
              </select>
            </>
          )}

          <div className={styles.chkrow} style={{ marginTop: "var(--nqh-space-2)" }}>
            <input
              type="checkbox"
              id="pOT"
              checked={isOvertime}
              onChange={(e) => setIsOvertime(e.target.checked)}
            />
            <label htmlFor="pOT" style={{ margin: 0 }}>
              Tính là OT
            </label>
          </div>

          {/* [STAGED-COMMIT]: cùng lý do đổi màu với nút "Thêm" — chỉ đẩy vào
              danh sách chờ ghi, không còn là hành động ghi thật. */}
          <button
            type="button"
            className={`${styles.btn} ${styles.btnSecondary}`}
            style={{ marginTop: "var(--nqh-space-3)", width: "100%" }}
            disabled={isCommitting}
            aria-disabled={isCommitting}
            onClick={handleStageHours}
          >
            Thêm khối giờ
          </button>

          {/* [STAGED-COMMIT]: danh sách chờ ghi — "Thêm"/"Thêm khối giờ" gộp
              chung vào đây (bất kể theo mã ca hay khối giờ tự do), xoá được
              từng mục TRƯỚC khi bấm "Xong"; mục còn `error` là mục server đã
              từ chối ở lần "Xong" TRƯỚC (giữ lại để sửa/bỏ, không mất trắng). */}
          {staged.length > 0 && (
            <>
              <div className={styles.popoverLabel} style={{ marginTop: "var(--nqh-space-4)" }}>
                Sẽ ghi khi bấm "Xong" ({staged.length})
              </div>
              <div>
                {staged.map((item) => (
                  <div
                    key={item.id}
                    className={styles.shiftLine}
                    style={{ flexDirection: "column", alignItems: "stretch", gap: "var(--nqh-space-1)" }}
                  >
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--nqh-space-2)" }}>
                      {/* PO 2026-09-13: "Sửa cho format của ca ở section thêm
                          giống format của ca đã gán để đồng nhất về UI" — bọc
                          lại đúng cấu trúc chip đã dùng cho "Ca đã gán trong
                          ngày" (.shiftChip + .shiftChipInline + màu theo
                          contentChipClass/shiftChipDayOff), thay vì text trần
                          không nền màu. Dòng CHÍNH "Mã · giờ" nằm trong
                          .shiftChipLine1; `branchName` ("Làm tại: {cơ sở}",
                          LUÔN có giá trị) là flex item kế tiếp của chip
                          (flex-column gốc) — tự xuống dòng riêng. */}
                      <span
                        className={`${styles.shiftChip} ${styles.shiftChipInline} ${
                          item.isDayOff
                            ? styles.shiftChipDayOff
                            : contentChipClass({
                                shift_code: item.shiftCode,
                                shift_type: item.shiftType,
                                is_overtime: item.isOvertime,
                              })
                        }`}
                      >
                        <span className={styles.shiftChipLine1}>{item.label}</span>
                        {item.branchName && (
                          <span className={styles.shiftLineBranch}>Làm tại: {item.branchName}</span>
                        )}
                      </span>
                      {/* PO 2026-09-13: "Đưa thời gian ra cột riêng ở giữa vì
                          popover đang rộng, thay vì thêm hàng" — khoảng ngày
                          (`subLabel`) TRƯỚC ĐÓ là 1 dòng phụ xếp DƯỚI bên
                          trong chip (thêm hàng); giờ popover đã rộng 440px,
                          chuyển thành CỘT riêng nằm GIỮA chip và nút "Xoá"
                          trong CÙNG 1 hàng ngang — `flex:1` + `text-align:
                          center` khiến nó chiếm phần không gian còn lại và tự
                          canh giữa, không cần đo px thủ công. */}
                      {item.subLabel && (
                        <span
                          className={`${styles.txtSm} ${styles.muted}`}
                          style={{ flex: "1 1 auto", textAlign: "center" }}
                        >
                          {item.subLabel}
                        </span>
                      )}
                      <button
                        type="button"
                        className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`}
                        disabled={isCommitting}
                        onClick={() => removeStaged(item.id)}
                      >
                        Xoá
                      </button>
                    </div>
                    {item.error && (
                      <span className={styles.txtSm} style={{ color: "var(--nqh-chili)" }}>
                        {item.error}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        {/* Cảnh báo trước khi cho đóng popover với lựa chọn combobox chưa bấm
            "Thêm" VÀ/HOẶC mục trong danh sách chờ ghi chưa bấm "Xong"
            [STAGED-COMMIT] — thay vì onClose() thẳng. */}
        {pendingDiscardWarning && (
          <div className={styles.popoverBody}>
            <div className={`${styles.banner} ${styles.bannerWarning}`} role="alert">
              <span className="ico" aria-hidden="true">
                <WarnIcon />
              </span>
              <span className={styles.popoverNote}>{discardWarningMessage()}</span>
            </div>
            <div style={{ display: "flex", gap: "var(--nqh-space-2)", justifyContent: "flex-end", marginTop: "var(--nqh-space-2)" }}>
              <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={cancelDiscardWarning}>
                Quay lại
              </button>
              <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={confirmDiscardAndClose}>
                Đóng, bỏ lựa chọn
              </button>
            </div>
          </div>
        )}
        {/* [2f] BR-SCHD-013: xác nhận "Nghỉ hàng loạt" — bắt buộc trước khi ghi
            (wireframe [2f]), cùng khuôn banner+2-nút với cảnh báo huỷ lựa chọn
            ở trên. */}
        {bulkOffConfirming && (
          <div className={styles.popoverBody}>
            <div className={`${styles.banner} ${styles.bannerWarning}`} role="alert">
              <span className="ico" aria-hidden="true">
                <WarnIcon />
              </span>
              <span className={styles.popoverNote}>
                Sẽ bỏ hết ca trong {dayCount} ngày × {targetEmployeeIds.length} nhân viên (
                {formatDayMonth(rangeStart)}–{formatDayMonth(rangeEnd)}). Các ô trống trong vùng
                chọn bị bỏ qua.
              </span>
            </div>
            <div style={{ display: "flex", gap: "var(--nqh-space-2)", justifyContent: "flex-end", marginTop: "var(--nqh-space-2)" }}>
              <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={cancelBulkOff}>
                Huỷ
              </button>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnPrimary}`}
                disabled={bulkOffMutation.isPending}
                onClick={confirmBulkOff}
              >
                {bulkOffMutation.isPending ? "Đang lưu…" : "Xác nhận nghỉ"}
              </button>
            </div>
          </div>
        )}
        {/* [R42] (2026-09-15): kết quả MỘT PHẦN của "Nghỉ (bỏ hết ca)" — xem
            khai báo `restLockedNote` + `handleRest` phía trên. Đặt NGAY TRÊN
            `.popoverFoot` (cạnh nút [Nghỉ (bỏ hết ca)]) — cùng vị trí "dòng
            giải thích trực tiếp trong popup" đã dùng cho R42_EXPLANATION_NOTE
            phía trên (D-7 `.unpubPopupNote`), không phải toast/tooltip. */}
        {restLockedNote && !isRangeMode && (
          <div className={styles.popoverBody} style={{ paddingTop: 0 }}>
            <span className={`${styles.txtSm} ${styles.muted}`}>{restLockedNote}</span>
          </div>
        )}
        {/* [RESIZE] Việc 2: tay cầm kéo-giãn — góc dưới-phải, tách biệt hẳn
            popoverHead (tay cầm kéo-thả ở góc trên) để 2 thao tác không giẫm
            lên nhau. stopPropagation trong handleResizeMouseDown là chốt chặn
            thứ 2 (chắc chắn, không chỉ dựa vào 2 vùng không chồng nhau).
            Đặt TRƯỚC .popoverFoot trong DOM (không phải sau) để giữ bất biến
            "foot luôn là phần tử con cuối cùng" (test DOM thật) — vị trí
            hiển thị không đổi vì handle là position:absolute + z-index đè
            lên foot, không phụ thuộc thứ tự DOM. */}
        <div
          className={styles.popoverResizeHandle}
          role="presentation"
          aria-hidden="true"
          title="Kéo để đổi kích thước"
          onMouseDown={handleResizeMouseDown}
        />
        <div className={styles.popoverFoot}>
          {isRangeMode ? (
            <button
              type="button"
              className={`${styles.btn} ${styles.btnSecondary}`}
              disabled={bulkOffMutation.isPending || isCommitting}
              onClick={startBulkOff}
            >
              Nghỉ hàng loạt
            </button>
          ) : (
            <button
              type="button"
              className={`${styles.btn} ${styles.btnSecondary}`}
              disabled={cellMutation.isPending || assigned.length === 0 || isCommitting}
              onClick={handleRest}
            >
              Nghỉ (bỏ hết ca)
            </button>
          )}
          {/* [STAGED-COMMIT]: "Xong" nay là hành động GHI THẬT khi còn mục
              trong danh sách chờ (commitStaged) — đổi btnSecondary → btnPrimary
              để khớp mức nổi bật với hệ quả thật của nút (xem lý do đổi màu ở
              "Thêm" phía trên, cùng 1 quyết định). */}
          <button
            type="button"
            className={`${styles.btn} ${styles.btnPrimary}`}
            disabled={isCommitting}
            onClick={handleDoneClick}
          >
            {isCommitting ? "Đang lưu…" : "Xong"}
          </button>
        </div>
      </div>

      {/* ══════════ [14] POPOVER cảnh báo vi phạm — neo NGAY DƯỚI [13] (không đóng [13])
           D-6: mode "Chặn" → chỉ lý do + [Đã hiểu], KHÔNG có [Vẫn thêm ca] ══════════ */}
      {warnBlock && (
        <div
          className={`${styles.popover} ${styles.popoverSub}`}
          style={{ top: warnPos.top, left: warnPos.left }}
          role="dialog"
          aria-modal="true"
          aria-label="Cảnh báo vi phạm ràng buộc"
        >
          <div className={styles.popoverBody}>
            <div className={`${styles.banner} ${styles.bannerWarning}`} role="alert">
              <span className="ico" aria-hidden="true">
                <WarnIcon />
              </span>
              <span className={styles.popoverNote}>
                {warnBlock.message}
                {warnBlock.violations.length > 0 && (
                  <>
                    {" "}
                    {warnBlock.violations.join("; ")}
                  </>
                )}
              </span>
            </div>
          </div>
          <div className={`${styles.popoverFoot} ${styles.popoverFootEnd}`}>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={() => setWarnBlock(null)}
            >
              Đã hiểu
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ShiftCellPopover;
